import React from 'react';
import { DATA_SRC_TYPES } from '../utils/Constants';
import { useDataSources } from './dataSources';
import { useGeoCmsSearch } from './geocms';
import {
	calcDistanceMatrix,
	calcDistanceMatrixProcess,
	useGoogleMapsApi
} from './gis';
import { SHOW_ALL, TABS_TO_DISPLAY } from './portletConfiguration';

export function useSearchProcess(
	geoCMSMap,
	searchFor,
	searchParameters,
	setLoading
) {
	const [, getDataSource] = useDataSources();

	const dataSrc = getDataSource(searchFor);

	const [
		geoCMSResults,
		refreshSearch,
		filters,
		setFilters,
		atLeastOne,
		setAtLeastOne
	] = useGeoCmsSearch(geoCMSMap, searchFor, searchParameters, setLoading);

	if (geoCMSResults && geoCMSResults.length === 0 && !atLeastOne) {
		setAtLeastOne(true);
		refreshSearch();
	} else if (geoCMSResults && geoCMSResults.length > 0 && atLeastOne) {
		setAtLeastOne(false);
	}

	const googleResults = useGoogleSearch(
		searchFor,
		searchParameters,
		setLoading,
		geoCMSMap
	);

	const urlResults = useURLSearch(
		searchFor,
		searchParameters,
		setLoading,
		geoCMSMap
	);

	let totalResults = [];

	if (
		(SHOW_ALL || dataSrc.type === DATA_SRC_TYPES.GeoCMS) &&
		geoCMSResults &&
		geoCMSResults.length > 0
	)
		totalResults = [...geoCMSResults];

	console.log("### GEOCMS:")
	console.log(geoCMSResults);
	console.log("---")

	if (
		(SHOW_ALL || dataSrc.type === DATA_SRC_TYPES.GooglePlaces) &&
		googleResults
	)
		totalResults = [...totalResults, ...googleResults];

	if ((SHOW_ALL || dataSrc.type === DATA_SRC_TYPES.URL) && urlResults)
		totalResults = [...totalResults, ...urlResults];

	// Remove SubAgencies if Agency is out of range - INC23126175
	totalResults = totalResults.filter((result) => {
		if (
			result.project != 'AgencyLocator' ||
			!result.hasOwnProperty('subAgencyId')
		)
			return true;

		return totalResults
			.filter((otherResult) => !otherResult.hasOwnProperty('subAgencyId'))
			.some((otherResult) => result.agencyId === otherResult.agencyId);
	});
	return [totalResults, filters, setFilters];
}

export function useURLSearch(searchFor, searchParameters, setLoading) {
	const [DATA_SRCS, getDataSource] = useDataSources();

	const [searchResults, setSearchResults] = React.useState(null);

	const google = useGoogleMapsApi();

	React.useEffect(() => {
		const dataSrcs = SHOW_ALL
			? DATA_SRCS.filter(
				(dataSrc) =>
					dataSrc.type === DATA_SRC_TYPES.URL &&
					TABS_TO_DISPLAY.includes(dataSrc.key)
			)
			: [getDataSource(searchFor)];

		let validateDataSrcType = true;

		if (dataSrcs.length === 1 && dataSrcs[0].type !== DATA_SRC_TYPES.URL) {
			validateDataSrcType = false;
		}

		if (
			searchParameters &&
			searchFor &&
			dataSrcs &&
			dataSrcs.length > 0 &&
			validateDataSrcType
		) {
			setLoading(true);

			const lat = searchParameters?.lat;
			const lng = searchParameters?.lng;

			let totalResults = [];

			const projectsToProcess = dataSrcs.length;

			let processedProjects = 0;

			for (let i = 0; i < dataSrcs.length; i++) {
				const dataSrc = dataSrcs[i];

				let url = dataSrc.processURL(lat, lng);

				fetch(url)
					.then((response) => response.json())
					.then((data) => {
						const results = data[dataSrc.resultsListProp];

						totalResults = [...totalResults, ...results];

						if (processedProjects === projectsToProcess - 1) {
							calcDistanceMatrixProcess(
								google,
								totalResults,
								0,
								lat,
								lng,
								setSearchResults,
								setLoading,
								dataSrc
							);
						} else {
							processedProjects++;
						}
					})
					.catch((error) => {
						console.error('Error:', error);
						setLoading(false);
					});
			}
		}
	}, [searchFor, searchParameters]);

	return searchResults;
}

export function useGoogleSearch(
	searchFor,
	searchParameters,
	setLoading,
	geoCMSMap
) {
	const [DATA_SRCS, getDataSource] = useDataSources();

	const [searchResults, setSearchResults] = React.useState(null);

	const google = useGoogleMapsApi();

	React.useEffect(() => {
		const dataSrcs = SHOW_ALL
			? DATA_SRCS.filter(
				(dataSrc) =>
					dataSrc.type === DATA_SRC_TYPES.GooglePlaces &&
					TABS_TO_DISPLAY.includes(dataSrc.key)
			)
			: [getDataSource(searchFor)];

		let validateDataSrcType = true;

		if (
			dataSrcs.length === 1 &&
			dataSrcs[0].type !== DATA_SRC_TYPES.GooglePlaces
		) {
			validateDataSrcType = false;
		}

		if (google && geoCMSMap && dataSrcs && dataSrcs.length > 0) {
			setLoading(true);

			const latLng = new google.maps.LatLng(
				geoCMSMap.map.getCenter().lat(),
				geoCMSMap.map.getCenter().lng()
			);

			const places = new google.maps.places.PlacesService(
				document.createElement('div')
			);

			let totalResults = [];

			const projectsToProcess = dataSrcs.length;

			let processedProjects = 0;

			for (let i = 0; i < dataSrcs.length; i++) {
				const dataSrc = dataSrcs[i];

				const request = {
					query: dataSrc.query,
					fields: ['name', 'geometry'],
					location: latLng
				};

				places.textSearch(request, (results, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) {
						totalResults = [...totalResults, ...results];

						if (processedProjects === projectsToProcess - 1) {
							calcDistanceMatrixProcess(
								google,
								totalResults,
								0,
								latLng.lat(),
								latLng.lng(),
								setSearchResults,
								setLoading,
								dataSrc
							);
						} else {
							processedProjects++;
						}
					}
				});
			}
		}
	}, [google, geoCMSMap, searchFor]);

	return searchResults;
}
